import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { CustomBranding } from '../../types';

interface SplashScreenProps {
  currentSong: any;
  countdown: number;
  show: boolean;
  branding?: CustomBranding | null; // Optional override: host branding
}

const SplashScreen: React.FC<SplashScreenProps> = ({ currentSong, countdown, show, branding }) => {
  const { userData } = useAuth();
  const subscription = useSubscription();

  // Log only when visibility changes, not every countdown tick
  useEffect(() => {
    if (show) {
      console.log('SplashScreen visible:', { countdown, currentSong });
    }
  }, [show]);

  // Check if user has pro subscription and custom branding enabled
  // Prefer provided branding (host branding) when available; otherwise fall back to viewer-based branding
  const hasProAccess = subscription.tier === 'pro-subscription' && subscription.isActive;
  const viewerBranding = hasProAccess && userData?.customBranding?.enabled ? userData.customBranding : null;
  const customBranding = branding ?? viewerBranding;

  const backgroundStyle = customBranding?.backgroundUrl 
    ? {
        backgroundImage: `url(${customBranding.backgroundUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    : {};

  const fontStyle = customBranding?.fontFamily 
    ? { fontFamily: customBranding.fontFamily }
    : {};
  // Always render the wrapper div but toggle visibility via CSS.
  // This prevents React from adding/removing DOM nodes, which avoids
  // the removeChild error caused by browser extensions mutating the DOM tree.
  const splashNode = (
    <div
      className="fixed inset-0 bg-black/85 text-white flex flex-col items-center justify-center text-center transition-opacity duration-500"
      style={{
        ...backgroundStyle,
        zIndex: 2147483647,
        opacity: show ? 1 : 0,
        pointerEvents: show ? 'auto' : 'none',
        visibility: show ? 'visible' : 'hidden',
      }}
    >
      {/* Debug border to ensure splash overlay is visible over other elements */}
      <div style={{ position: 'absolute', inset: 0, border: '2px dashed rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

      {/* Background overlay for readability when using custom background */}
      {customBranding?.backgroundUrl && (
        <div className="absolute inset-0 bg-black/60"></div>
      )}

      {/* Default QrOke logo or custom logo */}
      {customBranding?.logoUrl ? (
        <img
          src={customBranding.logoUrl}
          alt={customBranding.venueName || 'Venue Logo'}
          className="absolute top-8 left-8 h-16 md:h-20 object-contain z-10"
        />
      ) : (
        <img
          src="/Qroke Logo.png"
          alt="QrOke Icon"
          className="absolute w-1/2 h-1/2 object-contain opacity-10"
        />
      )}

      {/* Watermark */}
      {customBranding?.watermarkUrl && (
        <img
          src={customBranding.watermarkUrl}
          alt="Watermark"
          className="absolute bottom-8 right-8 h-12 object-contain opacity-70 z-10"
        />
      )}

      {/* Main content */}
      <div className="relative z-10 max-w-5xl mx-auto px-8" style={fontStyle}>
        {/* Venue name */}
        {customBranding?.venueName && (
          <div
            className="text-2xl md:text-3xl font-bold mb-4 opacity-90"
            style={{ color: customBranding.secondaryColor || '#A78BFA' }}
          >
            {customBranding.venueName}
          </div>
        )}

        {/* "Up Next" or custom message */}
        <div
          className="mb-6 text-4xl md:text-5xl font-display font-bold tracking-wide"
          style={{ color: customBranding?.primaryColor || '#8B5CF6' }}
        >
          {customBranding?.splashMessage || 'Up Next'}
        </div>

        {/* Song title */}
        <div className="text-4xl md:text-6xl mb-3 font-bold text-white">
          {currentSong?.title || 'No song title available'}
        </div>

        {/* Singer name */}
        <div
          className="text-2xl md:text-4xl mb-6"
          style={{ color: customBranding?.secondaryColor || '#A78BFA' }}
        >
          Singer: {currentSong?.singerName || currentSong?.requestedBy || 'Unknown'}
        </div>

        {/* Countdown */}
        <div
          className="text-5xl md:text-7xl font-extrabold"
          style={{ color: customBranding?.primaryColor || '#F59E0B' }}
        >
          Starting in {countdown ?? '...'}...
        </div>
      </div>
    </div>
  );

  // Always render (never return null) — prevents React DOM mutations
  // that conflict with browser extension DOM injections
  return splashNode;
};

export default SplashScreen;